import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, TertiaryButton, Text } from '@enechain/ecloud-designsystem'
import { FaFileArrowUpRegular } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

type Props = {
  onFileSelected: (file: File) => void
  /** ファイル仕様の説明 (列定義・上限など。 画面ごとに異なる。 改行は pre-line で保持)。 */
  specText: string
}

/**
 * CSV ファイルのドロップゾーン (design `admin/uploads/*.html` `#dropzone`
 * 相当)。 CSV アップロードウィザード各画面 (48点データ / 相対取引契約 /
 * 供給地点マスタ) で共用する。
 *
 * 枠内の文言 (タイトル / 条件 / ボタン / 隠し input のラベル) は全画面で
 * 同一なので component 内部で `common:upload.dropZone.*` を解決する
 * (`~/components/common/CsvUpload/CsvUploadStep1Panel` と同じ判断)。
 * 画面ごとに異なるファイル仕様 (`specText`) だけを props で受ける。
 *
 * 枠は design の `.ecl-upload-area` (= DS `UploadArea` の再現。 灰色の地・2px の破線・角丸 base・
 * 内側 24px・icon 32px・1 行目 16px/600・2 行目 14px 中強調・「ファイルを選択」の
 * TertiaryButton sm)。 UI 辞書 components/upload-wizard.md に従い、 枠全体はクリックできず
 * (role="button" / tabindex を付けない)、 ファイルを選ぶ導線はボタンだけ。 drag 中だけ
 * 背景 `Hover.lowEmphasis` / 実線の accent 枠 / ボタン disabled に変わる (hover 状態は無い)。
 * [DS差異] DS `UploadArea` を import しない理由: 隠し input の aria-label が固定の
 * "file-uploader" で上書きできず、 画面ごとに「CSV ファイルを選択」で input を特定する
 * 既存の操作 (テスト・支援技術) が成り立たなくなるため。 見た目の値は DS に合わせている。
 * ファイル仕様は枠の外、 直下に中央揃えで置く (design `.up-accept-spec`: 14px・低強調)。
 * 以前は枠の中に 10px で置いており、 選ぶ前に読むべき条件が小さすぎて読みにくかった。
 *
 * 素の `<input type="file">` を隠し要素として使い、 ボタンのクリックで発火する。D&D は
 * 素の DOM イベント (dragover/dragleave/drop) をハンドリングする。
 */
export const Dropzone = ({
  onFileSelected,
  specText,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFiles = (files: FileList | null): void => {
    const file = files?.[0]
    if (file === undefined) return
    onFileSelected(file)
  }

  return (
    <Flex flexDirection="column" gap={2}>
      <Box
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap={4}
        display="flex"
        padding={6}
        textAlign="center"
        background={isDragging ? 'Hover.lowEmphasis' : 'Surface.secondary'}
        border="2px"
        borderStyle={isDragging ? 'solid' : 'dashed'}
        borderColor={isDragging ? 'Border.accentPrimary' : 'Border.midEmphasis'}
        borderRadius="base"
        data-testid="csv-dropzone"
        onDragLeave={() => setIsDragging(false)}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragging(false)
          handleFiles(e.dataTransfer.files)
        }}
      >
        <FaFileArrowUpRegular
          boxSize={iconSize.uploadArea}
          color="Object.midEmphasis"
        />
        <Flex alignItems="center" flexDirection="column" gap={2}>
          <Text textStyle="sectionTitle" color="Text.highEmphasis">
            {t('common:upload.dropZone.csvTitle')}
          </Text>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('common:upload.dropZone.csvCondition')}
          </Text>
          <TertiaryButton
            disabled={isDragging}
            onClick={() => inputRef.current?.click()}
            size="sm"
          >
            {t('common:upload.dropZone.csvSelectButton')}
          </TertiaryButton>
        </Flex>
        {/* ecloud-designsystem に FileInput component が無いため、素の
          <input type="file"> を隠し要素として使う。 aria-label で getByLabelText が input を一意に特定できるようにする。 */}
        <input
          ref={inputRef}
          accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
          aria-label={t('common:upload.dropZone.fileInputLabel')}
          onChange={(e) => {
            handleFiles(e.target.files)
            e.target.value = ''
          }}
          style={{ display: 'none' }}
          type="file"
        />
      </Box>
      <Text
        color="Text.lowEmphasis"
        fontSize="md"
        lineHeight="1.7"
        textAlign="center"
        whiteSpace="pre-line"
      >
        {specText}
      </Text>
    </Flex>
  )
}
